import { axiosWrapper } from '@/services/axiosWrapper';
import { LocaleMessages } from 'vue-i18n';
import { getPaths, getValueFromPath } from '@/helpers/pathHelper';
import _log from '@/helpers/log';
import { locales } from '@/locales';
import translator from '@/i18n';

export default {

  /**
   * This function aggregates all locales modules into i18n locales.
   * Locales modules file must be {keyOfTheModule}__{locale}.json (there are 2 underscores),
   * or just {locale}.json.
   * A module with a 'keyOfTheModule' will be merged in the locale
   * as an object accessible via the 'keyOfTheModule' string.
   * Ex :
   * - en.json: {
   *  'trad1': 'value1'
   * }
   * - modulekey__en.json: {
   *  'trad2': 'value2'
   * }
   * These modules will be merged in the locale 'en' and they'll be like this: {
   *  'trad1': 'value1',
   *  'modulekey': {
   *    'trad2': 'value2'
   *  }
   * }
   */

  assignLocaleFiles(messages: LocaleMessages) {
    for (const key of Object.keys(locales)) {
      const matched = key.match(/([A-Za-z0-9-_]+)\./i);
      if (matched && matched.length > 1) {
        const fileName = matched[1];
        const fileNameSplit = fileName.split('__'); // DOUBLE underscore, to be able to use single underscores in keys
        if (fileNameSplit.length === 1) { // It's the dictionary base
          const locale = fileName;
          const fileContent = locales[key];
          messages[locale] = !messages[locale]
            ? fileContent
            : {
                ...messages[locale],
                ...fileContent
              };
        } else if (fileNameSplit.length === 2) { // we're in a submodule
          const locale = fileNameSplit[1];
          const localeKey = fileNameSplit[0];
          if (!messages[locale]) {
            messages[locale] = {};
          }
          if (messages[locale][localeKey]) {
            _log.logError({ message: `Locale key ${localeKey} already exists and won't be overriden` });
          } else {
            messages[locale][localeKey] = locales[key];
          }
        } else {
          _log.logError({ message: `${fileName} is not a valid locale file name` });
          continue;
        }
      }
    }
  },

  async assignServerMessages(messages: LocaleMessages): Promise<any> {
    const responses = await Promise.all([this.getFieldsLocales(), this.getSiteMapLocales(), this.getUnitsLocales(), this.getReferencesLocales()]);
    const locales = responses[0].data;
    const siteMap = responses[1].data;
    const units = responses[2].data;
    const references = responses[3].data;
    if (!locales) {
      _log.logError('No locales loaded');
      return;
    }
    const messagesAndPaths = [locales, siteMap, units, references].flatMap(value => getPaths(value));
    for (const msg of messagesAndPaths) {
      const key = msg.path.pop();
      if (key === undefined) {
        continue;
      }
      const targetObject: any = getValueFromPath(messages, msg.path, true);
      if (!(targetObject instanceof Object)) {
        continue;
      }
      targetObject[key] = msg.value;
    }
  },

  getFieldsLocales(): Promise<any> {
    return axiosWrapper.getFromLocalJson('fields_locales');
  },

  getSiteMapLocales(): Promise<any> {
    return axiosWrapper.getFromLocalJson('site_map_locales');
  },

  getUnitsLocales(): Promise<any> {
    return axiosWrapper.getFromLocalJson('units_locales');
  },

  getReferencesLocales(): Promise<any> {
    return axiosWrapper.getFromLocalJson('references_locales');
  },

  refreshLocaleMessages(localeMessages: LocaleMessages) {
    for (const locale of Object.keys(localeMessages)) {
      const localeMessage = localeMessages[locale];
      translator.mergeLocaleMessage(locale, localeMessage);
    }
  }
};
